import type { components } from "@/api/types";

/**
 * Узкие типы поверх сгенерированных.
 *
 * В контракте `market` и `risk_level` объявлены как `string`, а не enum
 * (`MarketFilter` существует, но используется только как query-параметр).
 * Сужение делается один раз здесь, на границе, а не по месту использования —
 * иначе каждый экран заводил бы свой набор строк.
 */
export type Market = "spot" | "futures";
export type RiskLevel = "low" | "medium" | "high";

/** Пятёрка роботов из `make seed`. Связь с макетом идёт по этому полю. */
export type RobotCode = "anchor" | "range" | "trend" | "delta" | "pulse";

/** Что робот анализирует (ТЗ §13). Подписи — в `robots.analytics.ai.signal.*`. */
export type AiSignal =
  | "price_action"
  | "volume"
  | "momentum"
  | "volatility"
  | "market_structure"
  | "correlation"
  | "btc_dominance"
  | "market_sentiment"
  | "open_interest"
  | "funding_rate"
  | "liquidations";

/** Режим рынка в блоке «робот сейчас» (ТЗ §10). */
export type MarketRegime =
  | "trend_up"
  | "trend_down"
  | "range"
  | "high_volatility"
  | "low_liquidity";

export type ActivityLevel = "low" | "normal" | "high";
export type TradeSide = "long" | "short";

const MARKETS = new Set<string>(["spot", "futures"]);
const RISK_LEVELS = new Set<string>(["low", "medium", "high"]);
const AI_SIGNALS = new Set<string>([
  "price_action",
  "volume",
  "momentum",
  "volatility",
  "market_structure",
  "correlation",
  "btc_dominance",
  "market_sentiment",
  "open_interest",
  "funding_rate",
  "liquidations",
]);
const REGIMES = new Set<string>([
  "trend_up",
  "trend_down",
  "range",
  "high_volatility",
  "low_liquidity",
]);
const ACTIVITY = new Set<string>(["low", "normal", "high"]);
const SIDES = new Set<string>(["long", "short"]);

export function asMarket(value: string): Market {
  if (MARKETS.has(value)) return value as Market;
  console.warn(`[robot] неизвестный market: ${value}`);
  return "spot";
}

export function asRiskLevel(value: string): RiskLevel {
  if (RISK_LEVELS.has(value)) return value as RiskLevel;
  console.warn(`[robot] неизвестный risk_level: ${value}`);
  return "medium";
}

/**
 * Сигналы, которые фронт умеет подписать. Неизвестный код отбрасывается, а не
 * рисуется сырым: `open_interest_v2` в премиальной витрине выглядит как утечка
 * бэкенда, и лучше показать на одну карточку меньше.
 */
export function asAiSignals(values: string[]): AiSignal[] {
  return values.filter((value): value is AiSignal => AI_SIGNALS.has(value));
}

export function asMarketRegime(value: string): MarketRegime {
  if (REGIMES.has(value)) return value as MarketRegime;
  console.warn(`[robot] неизвестный regime: ${value}`);
  return "range";
}

export function asActivityLevel(value: string): ActivityLevel {
  if (ACTIVITY.has(value)) return value as ActivityLevel;
  console.warn(`[robot] неизвестный activity: ${value}`);
  return "normal";
}

export function asTradeSide(value: string): TradeSide {
  if (SIDES.has(value)) return value as TradeSide;
  console.warn(`[robot] неизвестная сторона сделки: ${value}`);
  return "long";
}

/**
 * Цвет робота по коду. В Figma коллекция `Bot/*` отдана не полностью —
 * переменной объявлен только `Bot/Range`, остальные сняты с палитры Foundations
 * и являются алиасами продуктовых цветов (DESIGN.md §6).
 *
 * Поле связи называется `code` (`RobotBrief.code`), а не `slug`.
 * Неизвестный код не должен ломать карточку — отсюда фолбэк на акцент.
 */
export function botColorVar(code: string): string {
  return `var(--color-bot-${code}, var(--color-action-primary))`;
}

/** Минимум полей, по которым робот опознаётся визуально. */
export type RobotBranding = Pick<RobotBrief, "code" | "name" | "image_url" | "accent_color">;

/**
 * Цвет робота с учётом того, что задано в админке.
 *
 * Токены `--color-bot-*` объявлены ровно под пять seed-кодов, поэтому робот,
 * заведённый оператором с любым другим кодом, раньше молча получал зелёный
 * акцент по умолчанию. Теперь цвет приходит из базы, а токен остаётся
 * фолбэком — пятёрка из макета выглядит как прежде.
 *
 * Значение уезжает в inline-`style`, поэтому его формат (`#rrggbb`) проверяет
 * бэкенд: доверять здесь строке из формы нельзя.
 */
export function robotColor(robot: Pick<RobotBrief, "code" | "accent_color">): string {
  return robot.accent_color ?? botColorVar(robot.code);
}

/**
 * Имя общего элемента перехода «строка каталога → экран робота» (FE-13).
 * Одно на обе стороны: разъехавшиеся имена молча отменили бы переход.
 */
/**
 * Показывать ли необязательный блок экрана робота (ТЗ §26).
 *
 * ОТСУТСТВУЮЩИЙ КЛЮЧ ОЗНАЧАЕТ «ПОКАЗЫВАТЬ». Оператор выключает блок явно, а
 * новый робот заводится с пустым словарём — и не должен открываться с
 * выключенной витриной. Правило живёт в одной функции, чтобы шесть мест не
 * повторяли `!== false` каждое по-своему.
 */
export function isBlockVisible(
  blocks: Record<string, boolean> | undefined,
  code: ShowcaseBlock,
): boolean {
  return blocks?.[code] !== false;
}

export function robotTransitionName(id: number): string {
  return `robot-${String(id)}`;
}

export type RobotBrief = components["schemas"]["RobotBrief"];
export type RobotCard = components["schemas"]["RobotCard"];
export type RobotDetail = components["schemas"]["RobotDetail"];
export type RobotAnalytics = components["schemas"]["RobotAnalytics"];
export type ShowcasePeriod = components["schemas"]["ShowcasePeriod"];
export type TradeItem = components["schemas"]["TradeItem"];
export type AssetWeight = components["schemas"]["AssetWeight"];
export type RobotAudience = components["schemas"]["RobotAudience"];
/** Строка подиума ТОП-3 (доработки §5). */
export type TopResult = components["schemas"]["TopResult"];
/** Коды необязательных блоков витрины (ТЗ §26). */
export type ShowcaseBlock =
  | "top_users"
  | "aum"
  | "win_rate"
  | "trades"
  | "distribution"
  | "now"
  // Отзывы (доработки §6). Выключается по роботу, потому что у нового робота
  // отзывов физически нет, а пустой блок «Отзывы» читается как поломка, а не
  // как «ещё никто не написал».
  | "comments";
export type ShowcaseReturns = components["schemas"]["ShowcaseReturns"];
export type ShowcaseStats = components["schemas"]["ShowcaseStats"];
export type RobotNow = components["schemas"]["RobotNow"];
export type RobotPricing = components["schemas"]["RobotPricing"];
export type RobotSocialStats = components["schemas"]["RobotSocialStats"];
export type LimitedOffer = components["schemas"]["LimitedOffer"];
export type CommentsSummary = components["schemas"]["CommentsSummary"];

/** Состояние кампании (доработки §2.1). Считает бэкенд, фронт только рисует. */
export type OfferState =
  | "scheduled"
  | "active"
  | "almost_full"
  | "full"
  | "expired"
  | "paused";

/** Ярлык предложения. Подписи — в `robots.limited.badge.*`. */
export type OfferBadge = "limited" | "special" | "boosted";

/**
 * Чем витрина объявляет показанную доходность (доработки §2.1, §4.1).
 *
 * Решает бэкенд — это поле робота, которое ставит оператор. Фронт только
 * подписывает: `robots.yieldType.*`.
 */
export type YieldType = "historical" | "target";

const YIELD_TYPES = new Set<string>(["historical", "target"]);

/**
 * Незнакомый код падает в `target`, а не в `historical`, и направление
 * фолбэка здесь принципиально: ошибиться в сторону «целевая» значит показать
 * прогноз прогнозом, ошибиться в другую — назвать прогноз фактом.
 */
export function asYieldType(value: string): YieldType {
  if (YIELD_TYPES.has(value)) return value as YieldType;
  console.warn(`[robot] неизвестный yield_type: ${value}`);
  return "target";
}

const OFFER_STATES = new Set<string>([
  "scheduled",
  "active",
  "almost_full",
  "full",
  "expired",
  "paused",
]);
const OFFER_BADGES = new Set<string>(["limited", "special", "boosted"]);

/**
 * Незнакомое состояние кампании не должно ронять каталог (правило ТЗ №1).
 * Падаем в `active`: карточка нарисуется обычной, а решение «принимать ли
 * деньги» всё равно принимает бэкенд через `accepts_entries`, а не эта строка.
 */
export function asOfferState(value: string): OfferState {
  return OFFER_STATES.has(value) ? (value as OfferState) : "active";
}

export function asOfferBadge(value: string): OfferBadge {
  return OFFER_BADGES.has(value) ? (value as OfferBadge) : "limited";
}
